// services/LeaveService.js
import {
  createDocument,
  updateDocument,
  deleteDocument,
  getDocument,
  getCollection,
} from "./FirebaseService";
import {
  collection,
  query,
  orderBy,
  startAfter as startAfterDoc,
  limit as limitFn,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../auth/firebase";

const COLLECTION = "leaves";

// Map form data to Firestore leave document
const mapToLeave = (data) => ({
  studentName: data.studentName || "",
  studentId: data.studentId || "",
  email: data.email || "",
  courseEnrolled: data.courseEnrolled || "",
  reason: data.reason || "",
  startDate: data.startDate || "",
  endDate: data.endDate || "",
  totalDays: data.totalDays || 0,
  signature: data.signature || "",
  signatureDate: data.signatureDate || new Date().toISOString(),
  supportingFile: data.supportingFile || "",
  status: data.status || "pending", // pending, approved, notApproved
  comments: data.comments || "",
  authorizedName: data.authorizedName || "",
  authorizedPosition: data.authorizedPosition || "",
  authorizedSignature: data.authorizedSignature || "",
  authorizedDate: data.authorizedDate || "",
  createdAt: data.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});
const mapToUpdateLeave = (data) => ({
  studentName: data.studentName || "",
  studentId: data.studentId || "",
  email: data.email || "",
  courseEnrolled: data.courseEnrolled || "",
  reason: data.reason || "",
  startDate: data.startDate || "",
  endDate: data.endDate || "",
  totalDays: data.totalDays || 0,
  signature: data.signature || "",
  signatureDate: data.signatureDate || new Date().toISOString(),
  supportingFile: data.supportingFile || "",
  status: data.status || "pending", // pending, approved, notApproved
  comments: data.comments || "",
  authorizedName: data.authorizedName || "",
  authorizedPosition: data.authorizedPosition || "",
  authorizedSignature: data.authorizedSignature || "",
  authorizedDate: new Date().toISOString(),
  leaveApplicationFile: data.leaveApplicationFile || "",
  createdAt: data.createdAt,
  updatedAt: new Date().toISOString(),
});

// Create a new leave request
export const createLeave = async (data, id) =>
  await createDocument(COLLECTION, mapToLeave(data), id);

// Update an existing leave request
export const updateLeave = async (id, data) =>
  await updateDocument(COLLECTION, id, mapToUpdateLeave(data));

// Update leave status only (approve/reject)
export const updateStatusLeave = async (id, data, updateStatus) =>
  await updateDocument(COLLECTION, id, { ...mapToLeave(data), status: updateStatus });

// Delete a leave request
export const deleteLeave = async (id) => await deleteDocument(COLLECTION, id);

// Get a single leave request by ID
export const getLeave = async (id) => await getDocument(COLLECTION, id);

// Get all leave requests
export const getAllLeaves = async () => await getCollection(COLLECTION);

// Paginated query for leave requests
export const getLeavesPage = async ({
  limit = 10,
  startAfter = null,
  searchText = "",
} = {}) => {
  const fieldsToSearch = ["studentName", "studentId", "email"];
  const resultsMap = new Map(); // avoid duplicates

  const runQuery = async (field) => {
    let q = query(collection(db, COLLECTION), orderBy("createdAt"), limitFn(limit));
    if (startAfter) q = query(q, startAfterDoc(startAfter));
    if (searchText) q = query(q, where(field, "==", searchText));

    const snapshot = await getDocs(q);
    snapshot.forEach((doc) => {
      resultsMap.set(doc.id, { id: doc.id, ...doc.data() });
    });
  };

  if (searchText) {
    await Promise.all(fieldsToSearch.map((field) => runQuery(field)));
  } else {
    await runQuery(fieldsToSearch[0]);
  }

  const data = Array.from(resultsMap.values());
  return data;
};
export const getPendingLeaveCount = async () => {
  try {
    const q = query(collection(db, "leaves"), where("status", "==", "pending"));
    const snapshot = await getDocs(q);
    return snapshot.size; // number of pending leaves
  } catch (error) {
    console.error("Failed to fetch pending leave count:", error);
    return 0;
  }
};